import { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ThermometerIcon, DropletsIcon, ActivityIcon,
  WindIcon, CloudIcon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { useTelemetry } from '../../hooks/useTelemetry';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';
import { tbToChartData, tbToCombinedChartData } from '../../utils/tbChartHelper';
import { thingsboardService } from '../../services/thingsboard';

import type { SensorDetailSidebarProps, TimeRange } from './SensorDetailSidebar';
import { TimeRangeTabs, ChartSection, ToggleSwitch, SensorHeader } from './SensorDetailSidebar';
import { useTranslation } from 'react-i18next';

// ─── SES Detail View ───
export function SESDetail({ sensor, onBack, onClose, onUpdateSensor }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const { data: realtimeData } = useTelemetry(deviceId, ['TVOCs', 'CO2', 'dust', 'temperature', 'humidity', 'switch']);

  const [tvocRange, setTvocRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [airRange, setAirRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [envRange, setEnvRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [isSwitchOn, setIsSwitchOn] = useState(false);

  useEffect(() => {
    const switchState = realtimeData?.['switch'];
    if (switchState && switchState.length > 0) {
      const val = switchState[0].value;
      setIsSwitchOn(String(val) === 'on');
    }
  }, [realtimeData]);

  const handleToggleSwitch = async () => {
    const newValue = !isSwitchOn;
    setIsSwitchOn(newValue); // Optimistic UI update
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_switch', { value: newValue });
    } catch (error) {
      console.error('Failed to send RPC command for switch:', error);
      setIsSwitchOn(!newValue); // Revert on failure
    }
  };

  const { data: histTvoc } = useTelemetryHistory(deviceId, ['TVOCs'], tvocRange);
  const { data: histAir } = useTelemetryHistory(deviceId, ['CO2', 'dust'], airRange);
  const { data: histEnv } = useTelemetryHistory(deviceId, ['temperature', 'humidity'], envRange);

  const finalTvocData = useMemo(() => {
    return tvocRange === 'realtime'
      ? tbToChartData(realtimeData, 'TVOCs', 'realtime', [])
      : tbToChartData(histTvoc, 'TVOCs', tvocRange, []);
  }, [tvocRange, realtimeData, histTvoc]);

  const finalAirData = useMemo(() => {
    return airRange === 'realtime'
      ? tbToCombinedChartData(realtimeData, 'CO2', 'co2', 'dust', 'dust', 'realtime', [])
      : tbToCombinedChartData(histAir, 'CO2', 'co2', 'dust', 'dust', airRange, []);
  }, [airRange, realtimeData, histAir]);

  const tempHumidData = useMemo(() => {
    return envRange === 'realtime'
      ? tbToCombinedChartData(realtimeData, 'temperature', 'temp', 'humidity', 'humidity', 'realtime', [])
      : tbToCombinedChartData(histEnv, 'temperature', 'temp', 'humidity', 'humidity', envRange, []);
  }, [envRange, realtimeData, histEnv]);

  const tvocValue = finalTvocData.length > 0 ? (finalTvocData[finalTvocData.length - 1].value || 0) : 0;
  const co2Value = finalAirData.length > 0 ? (finalAirData[finalAirData.length - 1].co2 || 0) : 0;
  const dustValue = finalAirData.length > 0 ? (finalAirData[finalAirData.length - 1].dust || 0) : 0;

  const tempValue = tempHumidData.length > 0 ? (tempHumidData[tempHumidData.length - 1].temp || 0) : 0;
  const humidValue = tempHumidData.length > 0 ? (tempHumidData[tempHumidData.length - 1].humidity || 0) : 0;

  const tvocAverage = finalTvocData.length > 0
    ? Math.round(finalTvocData.reduce((acc: number, curr: any) => acc + (curr.value || 0), 0) / finalTvocData.length)
    : 0;
  const co2Average = finalAirData.length > 0
    ? Math.round(finalAirData.reduce((acc: number, curr: any) => acc + (curr.co2 || 0), 0) / finalAirData.length)
    : 0;
  const dustAverage = finalAirData.length > 0
    ? Math.round(finalAirData.reduce((acc: number, curr: any) => acc + (curr.dust || 0), 0) / finalAirData.length)
    : 0;
  const tempAverage = tempHumidData.length > 0
    ? (tempHumidData.reduce((acc: number, curr: any) => acc + (curr.temp || 0), 0) / tempHumidData.length).toFixed(1)
    : '--';
  const humidAverage = tempHumidData.length > 0
    ? (tempHumidData.reduce((acc: number, curr: any) => acc + (curr.humidity || 0), 0) / tempHumidData.length).toFixed(1)
    : '--';

  const tvocStatus = tvocValue < 200 ? '좋음' : tvocValue < 400 ? '보통' : '나쁨';
  const tvocColor = tvocValue < 200 ? 'text-emerald-400' : tvocValue < 400 ? 'text-amber-400' : 'text-red-400';
  const co2Status = co2Value < 600 ? '좋음' : co2Value < 1000 ? '보통' : '나쁨';
  const co2Color = co2Value < 600 ? 'text-emerald-400' : co2Value < 1000 ? 'text-amber-400' : 'text-red-400';
  const dustStatus = dustValue < 30 ? '좋음' : dustValue < 80 ? '보통' : '나쁨';
  const dustColor = dustValue < 30 ? 'text-emerald-400' : dustValue < 80 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="w-full flex flex-col gap-3 p-3 border-l min-h-full overflow-y-auto pb-10"
      style={{ borderColor: 'var(--mobile-border)', background: 'var(--mobile-bg-alt)' }}>
      <SensorHeader
        sensor={sensor}
        subtitle={t('sensor.smart_edge_sensor')}
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
        rightSlot={
          <ToggleSwitch isOn={isSwitchOn} onToggle={handleToggleSwitch} />
        }
      />

      {/* Quick Status Overview */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-3 gap-2 flex-shrink-0"
      >
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-medium font-bold mb-0.5">TVOC</div>
          <div className={`text-base font-mono font-bold ${tvocColor}`}>{tvocValue}</div>
          <div className={`text-[0.7rem] font-mono font-bold ${tvocColor}`}>{tvocStatus}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-medium font-bold mb-0.5">CO₂</div>
          <div className={`text-base font-mono font-bold ${co2Color}`}>{co2Value}</div>
          <div className={`text-[0.7rem] font-mono font-bold ${co2Color}`}>{co2Status}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-mono font-bold mb-0.5">미세먼지</div>
          <div className={`text-base font-mono font-bold ${dustColor}`}>{dustValue}</div>
          <div className={`text-[0.7rem] font-mono font-bold ${dustColor}`}>{dustStatus}</div>
        </div>
      </motion.div>

      {/* Temperature + Humidity Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel rounded-xl p-3 h-[280px] flex flex-col"
      >
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
            <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" />
            <span className="text-[0.9rem] font-bold text-slate-300">
              {t('sensor.indoor_temp_humidity')}
            </span>
          </div>
          <TimeRangeTabs value={envRange} onChange={setEnvRange} disabledRanges={!sensor.active ? ['realtime'] : []} />
        </div>

        {/* Current Values */}
        <div className="flex items-center gap-3 mb-2 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>온도</span>
            <span className="text-base font-mono font-bold text-rose-400">
              {tempHumidData.length > 0 ? tempValue.toFixed(1) : '--'}°C
            </span>
          </div>
          <div className="h-3.5 w-px bg-white/10" />
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>습도</span>
            <span className="text-base font-mono font-bold text-cyan-400">
              {tempHumidData.length > 0 ? humidValue.toFixed(1) : '--'}%
            </span>
          </div>
        </div>

        {/* Combined Chart */}
        <div className="flex-1 min-h-0 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tempHumidData}>
              <defs>
                <linearGradient id="sesSelTempGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="sesSelHumidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide />
              <YAxis yAxisId="left" hide domain={['dataMin - 1', 'dataMax + 1']} />
              <YAxis yAxisId="right" orientation="right" hide domain={['dataMin - 5', 'dataMax + 5']} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--mobile-bg-card)',
                  borderColor: 'var(--mobile-border-strong)',
                  fontSize: '0.7rem',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'temp') return [`${Number(value).toFixed(1)}°C`, '온도'];
                  return [`${Number(value).toFixed(1)}%`, '습도'];
                }}
              />
              <Area yAxisId="left" type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#sesSelTempGrad)" />
              <Area yAxisId="right" type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#sesSelHumidGrad)" strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Averages */}
        <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full bg-rose-400" />
            <span className="text-slate-500 text-[0.7rem]">평균</span>
            <span className="font-mono" style={{ color: 'var(--mobile-text-primary)' }}>{tempAverage}°C</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60" style={{ borderTop: '1px dashed #22d3ee' }} />
            <span className="text-slate-500 text-[0.7rem]">평균</span>
            <span className="font-mono" style={{ color: 'var(--mobile-text-primary)' }}>{humidAverage}%</span>
          </div>
        </div>
      </motion.div>

      <ChartSection
        icon={WindIcon}
        iconColor="text-emerald-400"
        title="TVOC"
        data={finalTvocData}
        timeRange={tvocRange}
        onTimeRangeChange={setTvocRange}
        disabledRanges={!sensor.active ? ['realtime'] : []}
        chartType="area"
        color="#34d399"
        gradientId="sesTvoc"
        className="h-[280px]"
        unit="ppb"
        stats={[
          { label: '현재', value: `${tvocValue.toFixed(1)} ppb`, color: tvocColor },
          { label: '평균', value: `${tvocAverage.toFixed(1)} ppb` }
        ]}
      />

      {/* Air Quality (CO2 + Dust) Combined Chart Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel rounded-xl p-3 h-[280px] flex flex-col"
      >
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CloudIcon className="w-3.5 h-3.5 text-orange-400" />
            <ActivityIcon className="w-3.5 h-3.5 text-pink-400" />
            <span className="text-[0.9rem] font-bold text-slate-300">
              {t('sensor.air_quality_co2_dust')}
            </span>
          </div>
          <TimeRangeTabs value={airRange} onChange={setAirRange} disabledRanges={!sensor.active ? ['realtime'] : []} />
        </div>

        <div className="flex-1 min-h-0 w-full relative pt-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={finalAirData}>
              <defs>
                <linearGradient id="sesAirCo2Grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fb923c" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#fb923c" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="sesAirDustGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f472b6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#f472b6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide />
              <YAxis yAxisId="left" hide domain={['dataMin - 100', 'dataMax + 100']} />
              <YAxis yAxisId="right" orientation="right" hide domain={['dataMin - 10', 'dataMax + 10']} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--mobile-bg-card)',
                  borderColor: 'var(--mobile-border-strong)',
                  fontSize: '0.7rem',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'co2') return [`${Number(value).toFixed(1)} ppm`, 'CO₂'];
                  return [`${Number(value).toFixed(1)} µg/m³`, '미세먼지'];
                }}
              />
              <Area yAxisId="left" type="monotone" dataKey="co2" stroke="#fb923c" strokeWidth={2} fillOpacity={1} fill="url(#sesAirCo2Grad)" />
              <Area yAxisId="right" type="monotone" dataKey="dust" stroke="#f472b6" strokeWidth={1.5} fillOpacity={1} fill="url(#sesAirDustGrad)" strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-0.5 rounded-full bg-orange-400" />
            <span className="text-slate-500 text-[0.8rem] uppercase">CO₂</span>
            <span className={`font-mono font-bold ${co2Color}`}>{co2Value}</span>
            <span className="text-slate-500 text-[0.8rem]">({co2Average})ppm</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-0.5 rounded-full bg-pink-400 opacity-80" style={{ borderTop: '1px dashed #f472b6' }} />
            <span className="text-slate-500 text-[0.8rem] uppercase">PM2.5</span>
            <span className={`font-mono font-bold ${dustColor}`}>{dustValue}</span>
            <span className="text-slate-500 text-[0.8rem]">({dustAverage})µg/m³</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}



